import React, { useEffect, useRef, useState } from 'react';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AndroidIcon from '@mui/icons-material/Android';
import logo from '../../assets/logo.svg';
import './ClinicMateLanding.css';
import { useNavigate } from 'react-router';

// ---------------------------------------------------------------------------
// Content — edit freely. Anything marked "placeholder" should be swapped for
// real copy, a real contact address and a real testimonial before you ship.
// ---------------------------------------------------------------------------

const NAV_LINKS = [
  { label: 'Product', href: '#hero' },
  { label: 'Features', href: '#features' },
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
];

const FEATURES = [
  {
    title: 'Appointments & calendar',
    icon: CalendarMonthRoundedIcon,
    desc: 'See the whole day at a glance and mark attendance in a single tap, across every branch.',
  },
  {
    title: 'Patients',
    icon: PeopleAltRoundedIcon,
    desc: 'One record per patient — history, attendance and active plans, always current.',
  },
  {
    title: 'Branches',
    icon: LocationOnRoundedIcon,
    desc: 'Run every location from a single login, each with its own staff, services and schedule.',
  },
  {
    title: 'Services',
    icon: StorefrontRoundedIcon,
    desc: 'Keep every service, session type and price list organized and consistent.',
  },
  {
    title: 'Invoicing',
    icon: ReceiptLongRoundedIcon,
    desc: 'Raise and track invoices without switching to another tool.',
  },
  {
    title: 'Users & roles',
    icon: ManageAccountsRoundedIcon,
    desc: 'Decide exactly who on your team can see, book or change what.',
  },
  {
    title: 'Carry forward',
    icon: EventRepeatRoundedIcon,
    desc: 'Unused sessions and balances roll over automatically — no manual tracking.',
  },
];

const STEPS = [
  {
    title: 'Set up your branches and services',
    desc: 'Add each location, the services offered there, and how long a session runs.',
  },
  {
    title: 'Add your patients and your team',
    desc: 'Bring in existing patients and create accounts for therapists, front desk and admins.',
  },
  {
    title: 'Run your day from one screen',
    desc: 'Bookings, attendance, invoices and carry-forwards, handled without leaving ClinicMate.',
  },
];

// Prices below are placeholders for the Pro plan — set the real number
// before shipping. Basic is fixed per your brief (₹1,299/month, all
// features).
const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: '1,199',
    desc: 'Everything you need to run day-to-day clinic operations.',
    cta: 'Book a demo',
    features: [
      'Branches & multi-location management',
      'Patient records & history',
      'Appointments & calendar',
      'Services & pricing',
      'Invoicing',
      'Users & roles',
      'Carry forward',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '1,499',
    desc: 'Everything in Basic, plus staying connected on the go.',
    cta: 'Book a demo',
    badge: 'Most popular',
    baseFeature: 'Everything in Basic',
    features: [
      'WhatsApp integration for appointment reminders & updates',
      'Android app access for your team and Patients',
    ],
  },
];

// Reveals an element once it scrolls into view (fade + rise), then stops
// watching. Used to give every section its own entrance animation.
// Respects prefers-reduced-motion by showing content immediately.
function useRevealOnScroll(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

export default function ClinicMateLanding() {
  const [navOpen, setNavOpen] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const reduceMotionRef = useRef(false);
  const parallaxBgRef = useRef(null);
  const parallaxFgRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    reduceMotionRef.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotionRef.current) {
      setCheckedIn(true);
      return undefined;
    }

    const interval = setInterval(() => {
      setCheckedIn((prev) => !prev);
    }, 3200);

    return () => clearInterval(interval);
  }, []);

  // Cycles which "screen" the tour mockup shows, in sync with the sidebar's
  // active item, so the preview pops between modules automatically.
  const [sceneIndex, setSceneIndex] = useState(0);
  const [leavingScene, setLeavingScene] = useState(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const id = setInterval(() => {
      setSceneIndex((current) => {
        setLeavingScene(current);
        return (current + 1) % FEATURES.length;
      });
    }, 2600);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (leavingScene === null) return undefined;
    const timeout = setTimeout(() => setLeavingScene(null), 380);
    return () => clearTimeout(timeout);
  }, [leavingScene]);

  // -------------------------------------------------- Pricing section --
  const pricingSectionRef = useRef(null);
  const pricingBlobARef = useRef(null);
  const pricingBlobBRef = useRef(null);
  const [pricingVisible, setPricingVisible] = useState(false);

  useEffect(() => {
    const el = pricingSectionRef.current;
    if (!el) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPricingVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPricingVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ----------------------------------------- Per-section entrances --
  const [featuresRef, featuresVisible] = useRevealOnScroll(0.1);
  const [stepsRef, stepsVisible] = useRevealOnScroll();
  const [quoteRef, quoteVisible] = useRevealOnScroll();
  const [ctaRef, ctaVisible] = useRevealOnScroll(0.25);
  const [footerRef, footerVisible] = useRevealOnScroll(0.05);

  // A gentle drift on the tour mock as the features section scrolls by —
  // a lighter-weight cousin of the hero/pricing parallax, since the mock
  // is no longer position:sticky.
  const tourSectionRef = useRef(null);
  const tourMockRef = useRef(null);

  // A soft glow behind the final CTA that drifts slightly on scroll.
  const ctaGlowRef = useRef(null);

  // ---------------------------------------------------------------------
  // Consolidated scroll/parallax effect.
  // Combines what used to be four separate scroll listeners (hero,
  // pricing, tour, CTA) into a single listener with a single
  // requestAnimationFrame tick, so we only do one round of layout reads
  // (getBoundingClientRect) per scroll frame instead of four.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let ticking = false;

    const applyAllParallax = () => {
      const scrollY = window.scrollY;

      // Hero — background layer drifts down slower than the page (depth
      // cue), foreground card drifts up slightly faster for a subtle 3D
      // feel.
      if (parallaxBgRef.current) {
        parallaxBgRef.current.style.transform = `translate3d(0, ${scrollY * 0.18}px, 0)`;
      }
      if (parallaxFgRef.current) {
        parallaxFgRef.current.style.transform = `translate3d(0, ${scrollY * -0.08}px, 0)`;
      }

      // Pricing — distance the section has scrolled past the top of the
      // viewport, keeps the drift relative to the section, not the page.
      const pricingEl = pricingSectionRef.current;
      if (pricingEl) {
        const progress = -pricingEl.getBoundingClientRect().top;
        if (pricingBlobARef.current) {
          pricingBlobARef.current.style.transform = `translate3d(0, ${progress * 0.1}px, 0)`;
        }
        if (pricingBlobBRef.current) {
          pricingBlobBRef.current.style.transform = `translate3d(0, ${progress * -0.06}px, 0)`;
        }
      }

      // Tour mock
      const tourEl = tourSectionRef.current;
      if (tourEl && tourMockRef.current) {
        const progress = -tourEl.getBoundingClientRect().top;
        tourMockRef.current.style.transform = `translate3d(0, ${progress * -0.04}px, 0)`;
      }

      // Final CTA glow
      const ctaEl = ctaRef.current;
      if (ctaEl && ctaGlowRef.current) {
        const progress = -ctaEl.getBoundingClientRect().top;
        ctaGlowRef.current.style.transform = `translate3d(-50%, ${progress * 0.08}px, 0)`;
      }

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(applyAllParallax);
        ticking = true;
      }
    };

    applyAllParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  }
  return (
    <div className="cm-page">
      <a className="cm-skip-link" href="#hero">
        Skip to content
      </a>

      {/* -------------------------------------------------- Navigation -- */}
      <header className="cm-nav">
        <div className="cm-nav__inner">
          <a className="cm-brand" href="#hero">
            <img src={logo} alt="ClinicMate" className="cm-logo" fetchpriority="high" />
          </a>

          <nav className="cm-nav__links" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href}>
                {link.label}
              </a>
            ))}
          </nav>

          <div className="cm-nav__actions">
            <a className="cm-btn cm-btn--ghost" onClick={handleLoginClick}>
              Log in
            </a>
            <a className="cm-btn cm-btn--primary" href="#contact">
              Book a demo
            </a>
          </div>

          <button
            type="button"
            className="cm-nav__toggle"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((prev) => !prev)}
          >
            {navOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
          </button>
        </div>

        {navOpen && (
          <div className="cm-nav__mobile">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href} onClick={() => setNavOpen(false)}>
                {link.label}
              </a>
            ))}
            <a className="cm-btn cm-btn--primary" href="#contact" onClick={() => setNavOpen(false)}>
              Book a demo
            </a>
          </div>
        )}
      </header>

      <main>
        {/* ------------------------------------------------------ Hero -- */}
        <section id="hero" className="cm-hero">
          <div className="cm-hero__copy">
            <span className="cm-hero__badge">
              <ChildCareRoundedIcon fontSize="small" />
              Built for pediatric rehab
            </span>
            <h1>
              One system for everything your pediatric rehab center runs on.
            </h1>
            <p className="cm-hero__sub">
              Branches, patients, appointments, invoicing and staff — ClinicMate
              keeps your pediatric rehab center&rsquo;s daily operations in
              one calm, connected place, so nothing depends on a spreadsheet
              or a sticky note.
            </p>
            <div className="cm-hero__actions">
              <a className="cm-btn cm-btn--primary cm-btn--lg" href="#contact">
                Book a demo
              </a>
              <a className="cm-btn cm-btn--text cm-btn--lg" href="#how-it-works">
                See how it works
              </a>
            </div>
            <p className="cm-hero__note">
              Built for front desks, therapists and administrators at
              pediatric rehab centers — no IT team required to get started.
            </p>
          </div>

          <div className="cm-hero__visual" aria-hidden="true">
            <div className="cm-parallax-layer cm-parallax-layer--bg" ref={parallaxBgRef}>
              <PulseLines />
            </div>
            <div className="cm-parallax-layer cm-parallax-layer--fg" ref={parallaxFgRef}>
              <DashboardMock checkedIn={checkedIn} />
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------- Features -- */}
        <section id="features" className="cm-features" ref={featuresRef}>
          <div className={'cm-features__head' + (featuresVisible ? ' is-visible' : '')}>
            <h2>Everything the front desk, therapists and admins touch.</h2>
            <p>
              Every part of ClinicMate your team opens during the day, laid
              out the way it actually sits in the product.
            </p>
          </div>

          <div className={'cm-tour' + (featuresVisible ? ' is-visible' : '')} ref={tourSectionRef}>
            <div className="cm-tour__leftCol">
              <div className="cm-tour__mock" ref={tourMockRef} aria-hidden="true">
                <div className="cm-tour__mockChrome">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="cm-tour__mockBody">
                  <nav className="cm-tour__sidebar">
                    {FEATURES.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.title}
                          className={
                            'cm-tour__sidebarItem' +
                            (index === sceneIndex ? ' is-active' : '')
                          }
                        >
                          <Icon fontSize="small" />
                          <span>{item.title}</span>
                          <span className="cm-tour__badge">{index + 1}</span>
                        </div>
                      );
                    })}
                  </nav>

                  <div className="cm-tour__sceneStack">
                    {leavingScene !== null && (
                      <TourScene
                        key={`leaving-${leavingScene}`}
                        item={FEATURES[leavingScene]}
                        variant="leaving"
                      />
                    )}
                    <TourScene
                      key={`current-${sceneIndex}`}
                      item={FEATURES[sceneIndex]}
                      variant="entering"
                    />
                  </div>
                </div>

                <div className="cm-tour__mockFooter">
                  <span>
                    <CheckRoundedIcon fontSize="inherit" /> Every module, one login
                  </span>
                  <span>
                    <CheckRoundedIcon fontSize="inherit" /> Role-based access for your team
                  </span>
                  <span>
                    <CheckRoundedIcon fontSize="inherit" /> No IT setup required
                  </span>
                </div>
              </div>

              <div className="cm-tour__phone" aria-hidden="true">
                <div className="cm-tour__phoneNotch" />
                <div className="cm-tour__phoneBubble">
                  <span className="cm-tour__phoneIcon">
                    <WhatsAppIcon fontSize="small" />
                  </span>
                  <div>
                    <strong>ClinicMate</strong>
                    <span>Reminder: Aanya&rsquo;s session today at 4:00 PM</span>
                  </div>
                </div>
                <div className="cm-tour__phoneBubble cm-tour__phoneBubble--muted">
                  <span className="cm-tour__phoneIcon">
                    <CheckRoundedIcon fontSize="small" />
                  </span>
                  <div>
                    <strong>Front desk</strong>
                    <span>Marked as checked in</span>
                  </div>
                </div>
              </div>
              <p className="cm-tour__phoneCaption">
                Appointment reminders sent straight to WhatsApp — included in
                the Pro plan.
              </p>
            </div>

            <ol className="cm-tour__list">
              {FEATURES.map((item, index) => (
                <li key={item.title} className="cm-tour__listItem">
                  <span className="cm-tour__number">{index + 1}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ----------------------------------------------- How it works -- */}
        <section id="how-it-works" className="cm-steps" ref={stepsRef}>
          <div className={'cm-features__head' + (stepsVisible ? ' is-visible' : '')}>
            <h2>Live in an afternoon, not a quarter.</h2>
            <p>Three steps between where you are now and a fully running pediatric rehab center.</p>
          </div>

          <ol className={'cm-steps__list' + (stepsVisible ? ' is-visible' : '')}>
            {STEPS.map((step, index) => (
              <li key={step.title} className="cm-steps__item">
                <span className="cm-steps__number">{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* --------------------------------------------------- Quote -- */}
        <section className="cm-quote" ref={quoteRef}>
          {/* Placeholder testimonial — replace with a real clinic quote. */}
          <blockquote className={quoteVisible ? 'is-visible' : ''}>
            <p>
              &ldquo;Before ClinicMate, our front desk ran on three
              spreadsheets and a notebook. Now the whole day fits on one
              screen.&rdquo;
            </p>
            <cite>Operations lead, multi-branch pediatric rehab center</cite>
          </blockquote>
        </section>

        {/* -------------------------------------------------- Pricing -- */}
        <section id="pricing" className="cm-pricing" ref={pricingSectionRef}>
          <div className="cm-pricing__blob cm-pricing__blob--a" ref={pricingBlobARef} aria-hidden="true" />
          <div className="cm-pricing__blob cm-pricing__blob--b" ref={pricingBlobBRef} aria-hidden="true" />

          <div className={'cm-pricing__head' + (pricingVisible ? ' is-visible' : '')}>
            <h2>Simple pricing for your pediatric rehab center.</h2>
            <p>
              Two plans, no confusing add-ons — just the tools your front
              desk and therapists actually use.
            </p>
          </div>

          <div className="cm-pricing__grid">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={
                  'cm-price-card' +
                  (plan.id === 'pro' ? ' cm-price-card--pro' : '') +
                  (pricingVisible ? ' is-visible' : '')
                }
              >
                {plan.badge && <span className="cm-price-card__badge">{plan.badge}</span>}

                <div className="cm-price-card__head">
                  <span className="cm-price-card__name">{plan.name}</span>
                  <div className="cm-price-card__amount">
                    <span className="cm-price-card__currency">₹</span>
                    {plan.price}
                    <span className="cm-price-card__period">/month</span>
                  </div>
                  <p className="cm-price-card__desc">{plan.desc}</p>
                </div>

                <ul className="cm-price-card__features">
                  {plan.baseFeature && (
                    <li className="is-base">
                      <CheckRoundedIcon fontSize="inherit" />
                      {plan.baseFeature}
                    </li>
                  )}
                  {plan.features.map((feature, index) => {
                    const isPro = plan.id === 'pro';
                    const FeatureIcon = isPro
                      ? index === 0
                        ? WhatsAppIcon
                        : AndroidIcon
                      : CheckRoundedIcon;
                    return (
                      <li key={feature} className={isPro ? 'is-highlight' : ''}>
                        <FeatureIcon fontSize="inherit" />
                        {feature}
                      </li>
                    );
                  })}
                </ul>

                <a
                  className={
                    'cm-btn cm-btn--lg cm-price-card__cta ' +
                    (plan.id === 'pro' ? 'cm-btn--primary' : 'cm-btn--ghost')
                  }
                  href="#contact"
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>

          <p className="cm-pricing__note">
            Need multiple branches or a custom rollout?{' '}
            <a href="#contact">Talk to sales</a> for a tailored plan.
          </p>
        </section>

        {/* -------------------------------------------------- Final CTA -- */}
        <section
          id="contact"
          className={'cm-cta' + (ctaVisible ? ' is-visible' : '')}
          ref={ctaRef}
        >
          <div className="cm-cta__glow" ref={ctaGlowRef} aria-hidden="true" />
          <h2>Give your front desk its calm back.</h2>
          <p>
            Book a walkthrough and see ClinicMate running with your rehab
            center&rsquo;s own branches, services and patients.
          </p>
          <div className="cm-hero__actions cm-hero__actions--center">
            <a className="cm-btn cm-btn--onDark cm-btn--lg" href="mailto:hello@clinicmate.app">
              Book a demo
            </a>
            <a className="cm-btn cm-btn--text cm-btn--onDark cm-btn--lg" href="mailto:hello@clinicmate.app">
              Talk to us
            </a>
          </div>
        </section>
      </main>

      {/* ------------------------------------------------------ Footer -- */}
      <footer className={'cm-footer' + (footerVisible ? ' is-visible' : '')} ref={footerRef}>
        <div className="cm-footer__main">
          <div className="cm-footer__top">
            <a className="cm-brand cm-brand--footer" href="#hero">
              <img src={logo} alt="ClinicMate" className="cm-logo cm-logo--footer" />
            </a>
            <p>The operating system for your pediatric rehab center&rsquo;s day.</p>
          </div>

          <div className="cm-footer__cols">
            <div>
              <h4>Product</h4>
              <a href="#hero">Overview</a>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
            </div>
            <div>
              <h4>Company</h4>
              <a href="#" onClick={(e) => e.preventDefault()}>Contact</a>
              <a href="#hero">About</a>
            </div>
            <div>
              <h4>Legal</h4>
              <a href="#" onClick={(e) => e.preventDefault()}>Privacy</a>
              <a href="#" onClick={(e) => e.preventDefault()}>Terms</a>
            </div>
          </div>
        </div>

        <div className="cm-footer__bottom">
          <span>© {new Date().getFullYear()} ClinicMate. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function PulseLines() {
  return (
    <svg
      className="cm-pulse-bg"
      viewBox="0 0 600 420"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M-20 210h80l20-90 40 180 30-140 25 50h465"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}

function TourScene({ item, variant }) {
  const Icon = item.icon;
  return (
    <div className={'cm-tour__scene cm-tour__scene--' + variant}>
      <div className="cm-tour__sceneHeader">
        <span className="cm-tour__sceneIcon">
          <Icon fontSize="small" />
        </span>
        <span>{item.title}</span>
      </div>
      <span className="cm-tour__previewBar cm-tour__previewBar--wide" />
      <span className="cm-tour__previewBar" />
      <div className="cm-tour__previewTiles">
        <span />
        <span />
      </div>
      <div className="cm-tour__previewRows">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function DashboardMock({ checkedIn }) {
  return (
    <div className="cm-mock" role="img" aria-label="Preview of the ClinicMate dashboard">
      <div className="cm-mock__chrome">
        <span />
        <span />
        <span />
        <span className="cm-mock__url">app.clinicmate.app</span>
      </div>

      <div className="cm-mock__body">
        <div className="cm-mock__welcome">
          <span className="cm-mock__eyebrow">Riverside Pediatric Rehab</span>
          <span className="cm-mock__heading">Today&rsquo;s overview</span>
        </div>

        <div className="cm-mock__tiles">
          <div className="cm-mock__tile cm-mock__tile--sky">
            <span>Today&rsquo;s appointments</span>
            <strong>18</strong>
          </div>
          <div className="cm-mock__tile cm-mock__tile--ink">
            <span>Active patients</span>
            <strong>246</strong>
          </div>
          <div className="cm-mock__tile cm-mock__tile--indigo">
            <span>New patients</span>
            <strong>7</strong>
          </div>
          <div className="cm-mock__tile cm-mock__tile--coral">
            <span>Total patients</span>
            <strong>1,204</strong>
          </div>
        </div>

        <div className="cm-mock__list">
          <div className="cm-mock__listHead">
            <span>Patient</span>
            <span>Session</span>
            <span>Status</span>
          </div>

          <div className="cm-mock__row">
            <span>A. Kapoor</span>
            <span>9:00 – 9:45</span>
            <span className="cm-mock__status cm-mock__status--done">
              <CheckRoundedIcon fontSize="inherit" /> Checked in
            </span>
          </div>

          <div className="cm-mock__row">
            <span>M. Osei</span>
            <span>9:45 – 10:30</span>
            <span
              className={
                'cm-mock__status' +
                (checkedIn ? ' cm-mock__status--done' : ' cm-mock__status--pending')
              }
            >
              {checkedIn ? (
                <>
                  <CheckRoundedIcon fontSize="inherit" /> Checked in
                </>
              ) : (
                'Scheduled'
              )}
            </span>
          </div>

          <div className="cm-mock__row">
            <span>S. Dutta</span>
            <span>10:30 – 11:15</span>
            <span className="cm-mock__status cm-mock__status--pending">Scheduled</span>
          </div>
        </div>
      </div>
    </div>
  );
}